import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { IconButton } from '@mui/material'
import PageLoading from '../../../components/PageLoading/PageLoading'
import InformationTable from '../../../components/Table/InformationTable'
import { dateToString, monthToString } from '../../../utils/date'
import MesRecienteSelector from '../../../components/form/MesRecienteSelector/MesRecienteSelector'
import {
  getEvaluacionesAlumno,
  updateEvaluacion
} from '../../../utils/api/evaluacion'
import { useNotification } from '../../../context/NotificationManager/NotificationManager'
import useAlumno from '../../../hooks/useAlumno'
import AlumnoHeader from '../../../components/AlumnoHeader/AlumnoHeader'
import ModalEditarEvaluacionAlumno from '../../../modals/ModalEditarEvaluacionAlumno/ModalEditarEvaluacionAlumno'
import css from './Evaluacion.module.css'

const FACE_ICONS = {
  MUY_POSITIVA: 'sentiment_very_satisfied',
  POSITIVA: 'sentiment_satisfied',
  NEUTRAL: 'sentiment_neutral',
  NEGATIVA: 'sentiment_dissatisfied',
  MUY_NEGATIVA: 'sentiment_very_dissatisfied'
}

const FaceIcon = ({ value }) => {
  if (!value || !FACE_ICONS[value]) return <span>-</span>
  return (
    <i className={`material-icons ${css.faceIcon}`} data-valoracion={value}>
      {FACE_ICONS[value]}
    </i>
  )
}

const Evaluacion = () => {
  const { id, nombre } = useAlumno()
  const notification = useNotification()
  const queryClient = useQueryClient()
  const [mes, setMes] = useState(monthToString(new Date()))
  const [isEditModalOpen, setEditModalOpen] = useState(false)
  const [selectedEvaluacion, setSelectedEvaluacion] = useState(null)

  const { isLoading, data: evaluacionList = [] } = useQuery({
    queryKey: ['evaluacion-alumnos', 'list', 'alumno', id, mes],
    enabled: !!mes,
    queryFn: () =>
      getEvaluacionesAlumno({ alumno: id, mes })
        .then(datos =>
          datos.sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
        )
        .catch(err => {
          notification.error({
            title: 'Error al recuperar las evaluaciones',
            content: err
          })
          return []
        })
  })

  const { isPending: isUpdating, mutate: actualizarEvaluacion } = useMutation({
    mutationFn: updateEvaluacion,
    onSuccess: () => {
      notification.success({
        title: 'Evaluación actualizada',
        content: 'Se ha actualizado la evaluación'
      })
    },
    onError: err => {
      notification.error({
        title: 'Error al actualizar la evaluación',
        content: err
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['evaluacion-alumnos', 'list', 'alumno', id, mes]
      })
      handleCloseEditModal()
    }
  })

  const handleOpenEditModal = evaluacion => {
    setSelectedEvaluacion({ ...evaluacion, nombre })
    setEditModalOpen(true)
  }

  const handleCloseEditModal = () => {
    setEditModalOpen(false)
    setSelectedEvaluacion(null)
  }

  const handleUpdateModal = payload => {
    if (isUpdating) return
    actualizarEvaluacion(payload)
  }

  return (
    <PageLoading isLoading={isLoading}>
      <div>
        <AlumnoHeader label='Evaluaciones' />
        <MesRecienteSelector
          className={css.mes}
          name='mes'
          label='Mes'
          value={mes}
          backwardOptions={2}
          forwardOptions={1}
          onChange={e => setMes(e.target.value)}
        />
      </div>

      <InformationTable
        details={[
          { title: 'Fecha', key: 'fecha' },
          { title: 'Profesor', key: 'profesor' },
          { title: 'Actitud', key: 'actitud', sortDisabled: true },
          { title: 'Limpieza', key: 'limpieza', sortDisabled: true },
          { title: 'Eficacia', key: 'asignaturas', sortDisabled: true },
          { title: 'Observaciones', key: 'observaciones', sortDisabled: true },
          { title: 'Opciones', key: 'actions', sortDisabled: true }
        ]}
        data={evaluacionList.map(evaluacion => ({
          fecha: dateToString(evaluacion.fecha),
          profesor: evaluacion.profesor || '-',
          actitud: <FaceIcon value={evaluacion.actitud} />,
          limpieza: <FaceIcon value={evaluacion.limpieza} />,
          asignaturas: (
            <div className={css.asignaturasList}>
              {(evaluacion.asignaturas || []).map((item, idx) => (
                <div key={idx} className={css.asignaturaRow}>
                  <span className={css.asignaturaNombre}>
                    {item.asignatura}:
                  </span>
                  <FaceIcon value={item.valoracion} />
                </div>
              ))}
            </div>
          ),
          observaciones: evaluacion.observaciones || '-',
          actions: (
            <IconButton
              color='warning'
              size='small'
              onClick={() => handleOpenEditModal(evaluacion)}
            >
              <i className='material-icons'>edit</i>
            </IconButton>
          )
        }))}
      />

      <ModalEditarEvaluacionAlumno
        open={isEditModalOpen}
        alumno={selectedEvaluacion}
        asignaturas={selectedEvaluacion?.asignaturas || []}
        onSubmit={handleUpdateModal}
        onClose={handleCloseEditModal}
        disabled={isUpdating}
      />
    </PageLoading>
  )
}

export default Evaluacion
